import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { Flex } from 'rebass';
import styled from 'styled-components';

import { ModalContainer, ModalButton, ModalSecondaryHeader, ModalClose, ModalCopy } from 'shared/modals';
import useErnie from 'shared/hooks/use-ernie';

import { InlineChainLocationSelector } from 'src/components/chain-location-selector';
import { useChainDispensaries, ChainDispensary } from 'src/components/chain-dispensary-select';
import { InlineBanner } from 'src/components/inline-banner/inline-banner';

type ModalStep = 'confirmation' | 'selection';

export type BulkCopyResult = {
  successCount: number;
  alreadyExistsCount: number;
  specialNotFoundCount: number;
};

// Builds the InlineBanner description summarizing bulk copy results, or undefined when there is nothing to report.
function buildBulkCopyResultMessage(result: BulkCopyResult): string | undefined {
  const formatLine = (count: number, suffix: string): string | null =>
    count > 0 ? `<strong>${count}</strong> banner ${count !== 1 ? 'copies' : 'copy'} ${suffix}` : null;

  return (
    [
      formatLine(result.successCount, 'made successfully.'),
      formatLine(result.alreadyExistsCount, 'already existed — skipped.'),
      formatLine(result.specialNotFoundCount, 'could not be copied — linked special not found.'),
    ]
      .filter(Boolean)
      .join('<br/>') || undefined
  );
}

type PublishToLocationsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (selectedDispensaryIds: string[]) => void;
  chainId: string;
  headerText: string;
  totalLocationCount: number;
  loading?: boolean;
  /** Modal title. Defaults to "Publish to locations" */
  title?: string;
  /** Description content. Uses a default template with headerText if not provided */
  description?: React.ReactNode;
  /** Confirm button text. Defaults to "Yes, publish changes" */
  confirmText?: string;
  /** Cancel button text. Defaults to "No, do not publish" */
  cancelText?: string;
  /** Action verb for warning text. Defaults to "update" */
  actionVerb?: string;
  /** Source dispensary ID to exclude from the location list */
  sourceDispensaryId?: string;
  /**
   * Optional render function to display a confirmation step.
   * When provided, the modal will show a "Next" button to advance to a confirmation view.
   * The function receives the selected dispensary IDs, full dispensary objects, and an onBack callback.
   */
  renderConfirmationContent?: (
    selectedIds: string[],
    dispensaries: ChainDispensary[],
    onBack: () => void
  ) => React.ReactNode;
  /** When set, displays a copy result summary in the modal after a bulk copy attempt. */
  bulkCopyResult?: BulkCopyResult | null;
  /** Called when the user navigates back from the confirmation step to the selection step. */
  onBack?: () => void;
};

/**
 * Modal component for selecting specific locations to publish/copy changes to.
 * Allows users to select a subset of chain locations or all locations.
 * Optionally supports a two-step flow with a confirmation view.
 */
export function PublishToLocationsModal({
  isOpen,
  onClose,
  onConfirm,
  chainId,
  headerText,
  totalLocationCount: _totalLocationCount,
  loading = false,
  title = 'Publish to locations',
  description,
  confirmText = 'Yes, publish changes',
  cancelText = 'No, do not publish',
  actionVerb: _actionVerb = 'update',
  sourceDispensaryId,
  renderConfirmationContent,
  bulkCopyResult,
  onBack,
}: PublishToLocationsModalProps): JSX.Element {
  const showErnie = useErnie();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [step, setStep] = useState<ModalStep>('selection');

  // Reset modal state when closed (handles both manual close and parent closing after confirm)
  useEffect(() => {
    if (!isOpen) {
      setSelectedIds([]);
      setStep('selection');
    }
  }, [isOpen]);

  // Fetch dispensaries data for the confirmation view
  const { chainDispensaries } = useChainDispensaries({ chainId });

  // Filter dispensaries to only include selected ones (excluding source)
  const selectedDispensaries = useMemo(() => {
    if (!chainDispensaries) {
      return [];
    }
    return chainDispensaries.filter((d) => selectedIds.includes(d.id) && d.id !== sourceDispensaryId);
  }, [chainDispensaries, selectedIds, sourceDispensaryId]);

  const hasConfirmationStep = Boolean(renderConfirmationContent);

  const handleSelectionChange = useCallback((newSelectedIds: (string | null)[]) => {
    setSelectedIds(newSelectedIds.filter((id): id is string => id !== null));
  }, []);

  const handleError = useCallback(
    (message: string) => {
      showErnie(message, 'danger');
    },
    [showErnie]
  );

  const handleNext = (): void => {
    if (selectedIds.length === 0) {
      showErnie('Please select at least one location.', 'danger');
      return;
    }
    setStep('confirmation');
  };

  const handleBack = (): void => {
    setStep('selection');
    onBack?.();
  };

  const handleConfirm = (): void => {
    if (selectedIds.length === 0) {
      showErnie('Please select at least one location.', 'danger');
      return;
    }
    onConfirm(selectedIds);
  };

  const handleClose = (): void => {
    setSelectedIds([]);
    setStep('selection');
    onClose();
  };

  const descriptionContent: React.ReactNode = description ?? (
    <span>
      Select which locations you want to apply the <strong>{headerText}</strong> settings to.
    </span>
  );

  const isSelectionStep = step === 'selection';
  const isConfirmationStep = step === 'confirmation';

  return (
    <ModalContainer
      data-test='publish-to-locations-modal'
      isOpen={isOpen}
      onRequestClose={handleClose}
      padding='32px 48px'
      width='750px'
    >
      <ModalSecondaryHeader fontSize='18px'>{title}</ModalSecondaryHeader>

      {isSelectionStep && (
        <>
          <ModalCopy>{descriptionContent}</ModalCopy>

          <SelectorContainer>
            <InlineChainLocationSelector
              chainId={chainId}
              initialSelectedIds={[]}
              onSelectionChange={handleSelectionChange}
              onError={handleError}
              excludeDispensaryIds={sourceDispensaryId ? [sourceDispensaryId] : []}
              maxHeight={350}
            />
          </SelectorContainer>

          <StyledFlex mt='20px' $gap='16px'>
            {hasConfirmationStep ? (
              <ModalButton
                data-testid='next-button'
                buttonContainerWidth='auto'
                width='auto'
                mt='0px'
                onClick={handleNext}
                disabled={selectedIds.length === 0}
              >
                Next
              </ModalButton>
            ) : (
              <ModalButton
                data-testid='confirm-publish-button'
                loading={loading}
                buttonContainerWidth='auto'
                width='auto'
                mt='0px'
                onClick={handleConfirm}
                disabled={selectedIds.length === 0}
              >
                {confirmText}
              </ModalButton>
            )}
            <ModalButton
              data-testid='cancel-publish-button'
              inverted
              buttonContainerWidth='auto'
              width='auto'
              mt='0px'
              onClick={handleClose}
            >
              {cancelText}
            </ModalButton>
          </StyledFlex>
        </>
      )}

      {isConfirmationStep && renderConfirmationContent && (
        <>
          {renderConfirmationContent(selectedIds, selectedDispensaries, handleBack)}

          {bulkCopyResult && (
            <InlineBanner
              severity='warning'
              padding='12px 16px'
              fontSize='14px'
              description={buildBulkCopyResultMessage(bulkCopyResult)}
            />
          )}

          <StyledFlex mt='20px' $gap='16px'>
            <ModalButton
              data-testid='confirm-publish-button'
              loading={loading}
              buttonContainerWidth='auto'
              width='auto'
              mt='0px'
              onClick={handleConfirm}
            >
              {confirmText}
            </ModalButton>
            <ModalButton
              data-testid='cancel-publish-button'
              inverted
              buttonContainerWidth='auto'
              width='auto'
              mt='0px'
              onClick={handleClose}
            >
              {cancelText}
            </ModalButton>
          </StyledFlex>
        </>
      )}

      <ModalClose onClick={handleClose} />
    </ModalContainer>
  );
}

const StyledFlex = styled(Flex)<{ $gap: string }>`
  gap: ${({ $gap }) => $gap};
`;

const SelectorContainer = styled.div`
  margin: 20px 0;
`;
